// One lane's card — a title plus four clickable metrics (Total Orders / Total Shipments /
// At Risk / Breached). Presentational only; a metric click just reports which (lane, metric) was
// picked, the page owns what happens next (the drawer). The two SLA metrics carry a hover
// breakdown of their reasons (at_risk_reasons / breach_reasons) when the summary sends one.
import { Card, Tooltip } from "antd";
import React from "react";
import { LaneCounts, LastMileLaneKey, MetricKey } from "../types";
import { reasonBreakdown } from "./sla_reasons";
import styles from "../index.module.scss";

interface Props {
  laneKey: LastMileLaneKey;
  label: string;
  counts: LaneCounts;
  onMetricClick: (lane: LastMileLaneKey, metric: MetricKey) => void;
}

const LaneCard: React.FC<Props> = ({ laneKey, label, counts, onMetricClick }) => {
  const stat = (
    metric: MetricKey,
    title: string,
    value: number,
    tooltip?: React.ReactNode,
    accentClass?: string // blue for the volume figures; amber/red only when the count is non-zero
  ) => {
    const block = (
      <div
        key={metric}
        className={`${styles.statBlock}${accentClass ? ` ${accentClass}` : ""}`}
        onClick={() => onMetricClick(laneKey, metric)}
        role="button"
        tabIndex={0}
      >
        <div className={styles.statLabel}>{title}</div>
        <div className={styles.statValue}>{value}</div>
      </div>
    );
    return tooltip ? (
      <Tooltip key={metric} title={tooltip}>
        {block}
      </Tooltip>
    ) : (
      block
    );
  };

  return (
    <Card size="small" title={label} className={styles.laneCard}>
      <div className={styles.statRow}>
        {stat("total_orders", "Total Orders", counts.total_orders, undefined, styles.statBlockInfo)}
        {stat("total_shipments", "Total Shipments", counts.total_shipments, undefined, styles.statBlockInfo)}
        {stat(
          "total_at_risk",
          "At Risk",
          counts.total_at_risk,
          reasonBreakdown(counts.at_risk_reasons),
          counts.total_at_risk > 0 ? styles.statBlockAtRisk : undefined
        )}
        {stat(
          "total_breached",
          "Breached",
          counts.total_breached,
          reasonBreakdown(counts.breach_reasons),
          counts.total_breached > 0 ? styles.statBlockBreached : undefined
        )}
      </div>
    </Card>
  );
};

export default LaneCard;
